/** Authoritative JavaScript embedded into source-game artifacts (and executed by browser tests).
 * Reads after destroy are neutral; new bindings fail. Normal release retains a buffered
 * edge, cancellation removes its owner's pending edge, and lifecycle reset clears all.
 */
export const SOURCE_GAME_INPUT_RUNTIME = String.raw`
const activeInputInstances=new Set();
let nextInputBindingId=0,inputReceiptSequence=0;
function publishInputControls(){
  const controls=[];
  for(const instance of activeInputInstances)controls.push(...instance.controls());
  post('carcade-controls-v1',{controls});
}
function createCarcadeInputRuntime(root,post,definitions){
  if(!definitions||typeof definitions!=='object'||Array.isArray(definitions))throw new TypeError('input definitions must be an object');
  const actions=new Map(),bindings=new Map(),contacts=new Map(),removers=[];
  let destroyed=false;
  for(const [name,definition] of Object.entries(definitions)){
    if(!/^[a-z][a-zA-Z0-9_-]{0,39}$/.test(name))throw new TypeError('invalid input action name');
    if(!definition||!['axis','button'].includes(definition.type))throw new TypeError('input action must have one axis or button type');
    const bufferMs=definition.bufferMs===undefined?100:definition.bufferMs;
    if(!Number.isInteger(bufferMs)||bufferMs<0||bufferMs>300)throw new TypeError('bufferMs must be an integer from 0 to 300');
    for(const field of ['keys','negativeKeys','positiveKeys'])if(definition[field]!==undefined&&(!Array.isArray(definition[field])||definition[field].some(key=>typeof key!=='string'||!key)))throw new TypeError(field+' must contain keyboard codes');
    actions.set(name,{type:definition.type,definition,bufferMs,owners:new Map(),pending:null});
  }
  function state(name,type){
    const action=actions.get(name);
    if(!action||action.type!==type)throw new TypeError('unknown or wrong-type '+type+' action: '+name);
    return action;
  }
  function receipt(name,phase,event){
    post('carcade-input-receipt-v1',{sequence:++inputReceiptSequence,action:name,phase,receivedAtMs:performance.now(),synthetic:!event.isTrusted});
  }
  function put(action,owner,value){
    if(action.type==='button'&&action.owners.size===0)action.pending={owner,expiresAt:performance.now()+action.bufferMs};
    action.owners.set(owner,value);
  }
  function release(owner,cancel,event){
    const contact=contacts.get(owner);
    if(!contact)return;
    contacts.delete(owner);
    const action=actions.get(contact.name);
    action.owners.delete(owner);
    if(cancel&&action.pending?.owner===owner)action.pending=null;
    if(event)receipt(contact.name,cancel?'cancel':'up',event);
    if(contact.pointerId!==undefined){try{contact.element.releasePointerCapture(contact.pointerId)}catch{}}
  }
  function listen(target,type,listener,options){
    target.addEventListener(type,listener,options);
    const remove=()=>target.removeEventListener(type,listener,options);
    return remove;
  }
  function axisValue(action){
    const touches=[...action.owners].filter(([owner])=>!owner.startsWith('key:'));
    if(touches.length){let strongest=0;for(const [,v]of touches)if(Math.abs(v)>Math.abs(strongest))strongest=v;return strongest}
    let negative=false,positive=false;
    for(const value of action.owners.values()){negative ||= value<0;positive ||= value>0}
    return negative===positive?0:positive?1:-1;
  }
  function horizontal(binding,x,previous=0){
    const rect=binding.element.getBoundingClientRect(),half=Math.max(1,rect.width/2);
    const normalized=Math.max(-1,Math.min(1,(x-rect.left-half)/half));
    const magnitude=Math.abs(normalized),sign=Math.sign(normalized);
    if(magnitude<=binding.deadZone)return 0;
    if(previous&&Math.sign(previous)!==sign&&magnitude<binding.deadZone+0.06)return 0;
    return sign*(magnitude-binding.deadZone)/(1-binding.deadZone);
  }
  function start(binding,owner,x,event,pointerId){
    if(destroyed||contacts.has(owner))return;
    event.preventDefault();
    const action=actions.get(binding.name),value=action.type==='axis'?horizontal(binding,x):1;
    contacts.set(owner,{name:binding.name,bindingId:binding.id,element:binding.element,pointerId,sign:Math.sign(value)});
    put(action,owner,value);receipt(binding.name,'down',event);
    if(pointerId!==undefined){try{binding.element.setPointerCapture(pointerId)}catch{}}
  }
  function move(owner,x,event){
    const contact=contacts.get(owner);if(!contact)return;
    event.preventDefault();
    const binding=bindings.get(contact.bindingId),action=actions.get(contact.name);
    if(action.type==='axis'){
      const value=horizontal(binding,x,contact.sign);
      if(value)contact.sign=Math.sign(value);
      action.owners.set(owner,value);
    }
    receipt(contact.name,'move',event);
  }
  function bind(name,element,type,deadZone=0.16){
    if(destroyed)throw new TypeError('input runtime is destroyed');
    state(name,type);
    if(!element||!root.contains(element))throw new TypeError('input control must belong to game root');
    if(!Number.isFinite(deadZone)||deadZone<0||deadZone>0.75)throw new TypeError('deadZone must be from 0 to 0.75');
    if([...bindings.values()].some(binding=>binding.element===element))throw new TypeError('input control already bound');
    const id=++nextInputBindingId,binding={id,name,element,deadZone,type};
    const oldTouchAction=element.style.touchAction;
    element.style.touchAction='none';bindings.set(id,binding);
    const off=[];
    off.push(listen(element,'contextmenu',event=>event.preventDefault()));
    if(typeof PointerEvent==='function'){
      off.push(listen(element,'pointerdown',event=>{if(event.button===0)start(binding,'pointer:'+event.pointerId,event.clientX,event,event.pointerId)}));
    }else{
      off.push(listen(element,'touchstart',event=>{for(const touch of event.changedTouches)start(binding,'touch:'+touch.identifier,touch.clientX,event)},{passive:false}));
      off.push(listen(element,'mousedown',event=>{if(event.button===0&&!event.sourceCapabilities?.firesTouchEvents)start(binding,'mouse:0',event.clientX,event)}));
    }
    let removed=false;
    binding.remove=()=>{
      if(removed)return;removed=true;
      for(const [owner,contact]of contacts)if(contact.bindingId===id)release(owner,true);
      for(const remove of off)remove();
      bindings.delete(id);element.style.touchAction=oldTouchAction;
      publishInputControls();
    };
    // Wait until all synchronous registrations and game layout have settled.
    requestAnimationFrame(publishInputControls);
    return binding.remove;
  }
  if(typeof PointerEvent==='function'){
    removers.push(listen(window,'pointermove',event=>move('pointer:'+event.pointerId,event.clientX,event),{passive:false}));
    for(const type of ['pointerup','pointercancel','lostpointercapture'])removers.push(listen(window,type,event=>release('pointer:'+event.pointerId,type!=='pointerup',event)));
  }else{
    removers.push(listen(window,'touchmove',event=>{for(const touch of event.changedTouches)move('touch:'+touch.identifier,touch.clientX,event)},{passive:false}));
    for(const type of ['touchend','touchcancel'])removers.push(listen(window,type,event=>{for(const touch of event.changedTouches)release('touch:'+touch.identifier,type==='touchcancel',event)}));
    removers.push(listen(window,'mousemove',event=>move('mouse:0',event.clientX,event),{passive:false}));
    removers.push(listen(window,'mouseup',event=>release('mouse:0',false,event)));
  }
  function keyboard(event,pressed){
    if(pressed&&event.repeat)return;
    const owner='key:'+event.code;
    for(const [name,action]of actions){
      const d=action.definition;
      const negative=d.negativeKeys?.includes(event.code),positive=d.positiveKeys?.includes(event.code);
      if(!(action.type==='button'?d.keys?.includes(event.code):negative||positive))continue;
      event.preventDefault();
      if(pressed){if(!action.owners.has(owner)){put(action,owner,action.type==='button'?1:negative&&positive?0:negative?-1:1);receipt(name,'down',event)}}
      else if(action.owners.delete(owner))receipt(name,'up',event);
    }
  }
  removers.push(listen(window,'keydown',event=>keyboard(event,true)),listen(window,'keyup',event=>keyboard(event,false)));
  function releaseAll(){
    for(const owner of [...contacts.keys()])release(owner,true);
    for(const action of actions.values()){action.owners.clear();action.pending=null}
  }
  removers.push(listen(window,'blur',releaseAll),listen(window,'pagehide',releaseAll),listen(document,'visibilitychange',()=>{if(document.hidden)releaseAll()}),listen(window,'resize',publishInputControls));
  const api=Object.freeze({
    bindHorizontalAxis(name,element,options={}){return bind(name,element,'axis',options.deadZone===undefined?0.16:options.deadZone)},
    bindButton(name,element){return bind(name,element,'button')},
    axis(name){return destroyed?0:axisValue(state(name,'axis'))},
    down(name){return destroyed?false:state(name,'button').owners.size>0},
    consumePress(name){
      if(destroyed)return false;
      const action=state(name,'button'),pending=action.pending;action.pending=null;
      return !!pending&&performance.now()<=pending.expiresAt;
    },
    releaseAll,
    controls(){return [...bindings.values()].map(binding=>{const r=binding.element.getBoundingClientRect();return {bindingId:binding.id,action:binding.name,kind:binding.type==='axis'?'horizontal-axis':'button',rect:{x:r.x/innerWidth,y:r.y/innerHeight,width:r.width/innerWidth,height:r.height/innerHeight}}})},
    destroy(){if(destroyed)return;destroyed=true;releaseAll();for(const binding of [...bindings.values()])binding.remove();for(const remove of removers)remove();activeInputInstances.delete(api);publishInputControls()}
  });
  activeInputInstances.add(api);return api;
}
`;
